import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Menu, Shield, UserCheck, UserX, Search, Filter, Download, Eye, Calendar, Mail, Phone, MapPin, Star } from 'lucide-react';
import { usersAPI, sitesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    phone?: string;
    siteId?: {
        _id: string;
        name: string;
    };
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    lastLogin?: string;
    address?: string;
    postcode?: string;
    city?: string;
    dateOfBirth?: string;
    gdprConsent: boolean;
    gdprConsentDate?: string;
    marketingConsent: boolean;
    preferredNotificationMethod: 'sms' | 'email' | 'both';
    profileImage?: string;
    notes?: string;
}

interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
    phone: string;
    siteId: string;
    isActive: boolean;
    address?: string;
    postcode?: string;
    city?: string;
    dateOfBirth?: string;
    gdprConsent: boolean;
    marketingConsent: boolean;
    preferredNotificationMethod: 'sms' | 'email' | 'both';
    notes?: string;
}

const emptyForm: UserFormData = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'staff',
    phone: '',
    siteId: '',
    isActive: true,
    address: '',
    postcode: '',
    city: '',
    dateOfBirth: '',
    gdprConsent: false,
    marketingConsent: false,
    preferredNotificationMethod: 'sms',
    notes: ''
};

const ROLE_OPTIONS = [
    { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-800' },
    { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-800' },
    { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-800' },
    { value: 'staff', label: 'Staff', color: 'bg-green-100 text-green-800' },
    { value: 'driver', label: 'Driver', color: 'bg-orange-100 text-orange-800' },
];

const getRoleBadge = (role: string) => {
    const found = ROLE_OPTIONS.find((r) => r.value === role);
    return found ? found.color : 'bg-gray-100 text-gray-800';
};

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [sites, setSites] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<UserFormData>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [siteFilter, setSiteFilter] = useState<string>('all');

    const token = useMemo(() => localStorage.getItem('token'), []);

    // Simple debounce function
    const debounce = (func: Function, delay: number) => {
        let timeoutId: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    // Debounce search to prevent excessive API calls
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchTerm(value);
        }, 500),
        []
    );

    const resetForm = () => {
        setEditingUser(null);
        setFormData(emptyForm);
    };

    const fetchSites = async () => {
    try {
        const response = await sitesAPI.getSites();
        // Handle new API response format { sites: [...] }
        const sitesArray = Array.isArray(response.data) ? response.data : response.data.sites || [];
        setSites(sitesArray);
    } catch (error: any) {
        console.error('Error fetching sites:', error);
        setSites([]);
    }
};

const fetchUsers = async () => {
    try {
        setLoading(true);
        const params: any = {};
        
        // Only add parameters if they're not 'all' and have values
        if (searchTerm && searchTerm.trim()) {
            params.search = searchTerm.trim();
        }
        
        if (roleFilter && roleFilter !== 'all') {
            params.role = roleFilter;
        }
        
        if (statusFilter && statusFilter !== 'all') {
            params.status = statusFilter === 'active' ? true : false;
        }
        
        if (siteFilter && siteFilter !== 'all') {
            params.siteId = siteFilter;
        }
        
        const response = await usersAPI.getUsers(params);
        
        // Handle different response formats
        const usersArray = Array.isArray(response.data) ? response.data : response.data.users || [];
        setUsers(usersArray);
    } catch (error: any) {
        console.error('Error fetching users:', error);
        toast.error(error.response?.data?.error || 'Failed to load users');
        setUsers([]);
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {
        fetchUsers();
        fetchSites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [searchTerm, roleFilter, statusFilter, siteFilter]);

    const openCreate = () => {
        resetForm();
        setShowModal(true);
    };

    const openEdit = (user: User) => {
        setEditingUser(user);
        setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            password: '',
            role: user.role || 'staff',
            phone: user.phone || '',
            siteId: user.siteId?._id || '',
            isActive: user.isActive ?? true,
            address: user.address || '',
            postcode: user.postcode || '',
            city: user.city || '',
            dateOfBirth: user.dateOfBirth ? format(new Date(user.dateOfBirth), 'yyyy-MM-dd') : '',
            gdprConsent: user.gdprConsent || false,
            marketingConsent: user.marketingConsent || false,
            preferredNotificationMethod: user.preferredNotificationMethod || 'sms',
            notes: user.notes || ''
        });
        setShowModal(true);
    };

    const saveUser = async () => {
        if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
            toast.error('First name, last name, and email are required');
            return;
        }

        if (!editingUser && !formData.password.trim()) {
            toast.error('Password is required for new users');
            return;
        }

        setSaving(true);
        try {
            if (editingUser) {
                // Update existing user
                await usersAPI.updateUser(editingUser._id, {
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    role: formData.role,
                    phone: formData.phone.trim(),
                    siteId: formData.siteId,
                    isActive: formData.isActive,
                    address: formData.address,
                    postcode: formData.postcode,
                    city: formData.city,
                    dateOfBirth: formData.dateOfBirth,
                    gdprConsent: formData.gdprConsent,
                    marketingConsent: formData.marketingConsent,
                    preferredNotificationMethod: formData.preferredNotificationMethod,
                    notes: formData.notes
                });
                toast.success('User updated successfully!');
            } else {
                // Create new user
                await usersAPI.createUser(formData);
                toast.success('User created successfully!');
            }
            
            setShowModal(false);
            setEditingUser(null);
            resetForm();
            fetchUsers();
        } catch (error: any) {
            console.error('Error saving user:', error);
            toast.error(error.response?.data?.error || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const deleteUser = async (userId: string, userName: string) => {
        const ok = window.confirm(`Delete user "${userName}"? This action cannot be undone.`);
        if (!ok) return;

        try {
            setDeletingId(userId);
            await usersAPI.deleteUser(userId);
            toast.success('User deleted successfully!');
            fetchUsers();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            toast.error(error.response?.data?.error || 'Failed to delete user');
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="sm:flex sm:justify-between sm:items-center mb-8">
                    <div className="sm:flex-auto">
                        <div className="flex items-center space-x-4">
                            <Link
                                to="/admin"
                                className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Dashboard
                            </Link>
                            <img src="/hmpos_logo.jpeg" alt="Logo" className="h-10 w-auto rounded" />
                            <div>
                                <p className="mt-2 text-sm text-gray-700">Manage staff accounts and roles</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
                        <div className="flex space-x-4">
                            <Link
                                to="/admin"
                                className="px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            >
                                <Menu className="h-5 w-5 mr-2" />
                                Admin Menu
                            </Link>
                            <button
                                onClick={openCreate}
                                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add User
                            </button>
                        </div>
                    </div>
                </div>

                {/* User Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                                <Shield className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Total Users</p>
                                <p className="text-2xl font-semibold text-gray-900">{(users || []).length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                                <UserCheck className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Active</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {(users || []).filter((u) => u.isActive !== false).length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                                <UserX className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Inactive</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {(users || []).filter((u) => u.isActive === false).length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                                <Shield className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Admins</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {(users || []).filter((u) => u.role === 'admin').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm mb-6">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        onChange={(e) => debouncedSearch(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg px-3 py-2 focus:border-primary-400 focus:outline-none transition-colors"
                                        placeholder="Search users..."
                                    />
                                </div>
                            </div>

                            {/* Role Filter */}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-primary-400 focus:outline-none transition-colors bg-white"
                                >
                                    <option value="all">All Roles</option>
                                    {ROLE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Status Filter */}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-primary-400 focus:outline-none transition-colors bg-white"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>

                            {/* Site Filter */}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Site</label>
                                <select
                                    value={siteFilter}
                                    onChange={(e) => setSiteFilter(e.target.value)}
                                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-primary-400 focus:outline-none transition-colors bg-white"
                                >
                                    <option value="all">All Sites</option>
                                    {sites.map((site) => (
                                        <option key={site._id} value={site._id}>{site.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">All Users</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Site
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Phone
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(users || []).length === 0 ? (
                                    <tr>
                                        <td className="px-6 py-6 text-sm text-gray-500" colSpan={7}>
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    (users || []).map((u) => (
                                        <tr key={u._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {u.firstName} {u.lastName}
                                                </div>
                                                <div className="text-xs text-gray-400">{u._id}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {u.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadge(
                                                        u.role
                                                    )}`}
                                                >
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {(() => {
                                                    if (u.siteId) {
                                                        if (typeof u.siteId === 'object' && u.siteId.name) {
                                                            return (
                                                                <div className="flex items-center">
                                                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                                                    <span className="text-sm text-gray-900">{u.siteId.name}</span>
                                                                </div>
                                                            );
                                                        } else if (typeof u.siteId === 'string') {
                                                            // Find site name from sites array
                                                            const site = sites.find(s => s._id === u.siteId);
                                                            return (
                                                                <div className="flex items-center">
                                                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                                                    <span className="text-sm text-gray-900">{site?.name || 'Unknown Site'}</span>
                                                                </div>
                                                            );
                                                        }
                                                    }
                                                    return (
                                                        <span className="text-sm text-gray-400 italic">No site assigned</span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {u.phone || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(u.isActive ?? true)
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                        }`}
                                                >
                                                    {(u.isActive ?? true) ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => openEdit(u)}
                                                    className="text-primary-600 hover:text-primary-900 mr-4"
                                                    title="Edit"
                                                >
                                                    <Edit className="h-4 w-4 inline" />
                                                </button>
                                                <button
                                                    onClick={() => deleteUser(u._id, `${u.firstName} ${u.lastName}`)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4 inline" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-[580px] max-w-[95vw]">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingUser ? 'Edit User' : 'Add New User'}
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-700">First Name</label>
                                <input
                                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-700">Last Name</label>
                                <input
                                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm text-gray-700">Email</label>
                                <input
                                    type="email"
                                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                                    value={formData.email}
                                    disabled={!!editingUser}
                                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                                />
                                {editingUser && (
                                    <p className="text-xs text-gray-400 mt-1">Email cannot be changed after creation</p>
                                )}
                            </div>
                            {!editingUser && (
                                <div className="col-span-2">
                                    <label className="block text-sm text-gray-700">Password</label>
                                    <input
                                        type="password"
                                        className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                                        value={formData.password}
                                        onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                                        placeholder="Minimum 6 characters"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm text-gray-700">Role</label>
                                <select
                                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                                    value={formData.role}
                                    onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value }))}
                                >
                                    {ROLE_OPTIONS.map((r) => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-700">Phone</label>
                                <input
                                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                                    value={formData.phone}
                                    onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                                    placeholder="Optional"
                                />
                            </div>
                            {editingUser && (
                                <div className="col-span-2">
                                    <label className="inline-flex items-center mt-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                                            className="rounded text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Active</span>
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={saveUser} disabled={saving}>
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
